import { CommonModule } from '@angular/common';
import { Component, inject, Input as RouterInput, ViewChild } from '@angular/core';
import { getIdToken } from '@angular/fire/auth';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { BaseComponent } from '../../shared/base.component';
import { UpdateDroneState } from '../../shared/state-interface';
import { SiteHeaderComponent } from '../../site-header/site-header.component';
import { DroneComponent } from '../shared/drone.component';
import { DroneService } from '../shared/drone.service';
import { RegisteredDrone } from '../shared/registered-drone.model';

@Component({
    selector: 'app-update-drone',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatInputModule,
        MatButtonModule,
        MatDatepickerModule,
        SiteHeaderComponent,
        DroneComponent
    ],
    templateUrl: './update-drone.component.html',
    styleUrl: './update-drone.component.css'
})
export class UpdateDroneComponent extends BaseComponent {

    @RouterInput() id: number = -1;
    @ViewChild(DroneComponent) droneComponent!:DroneComponent;

    private droneService: DroneService = inject(DroneService);

    private static _retrievingDrone = "Retrieving Drone";
    private static _retrievingDroneError = `${UpdateDroneComponent._retrievingDrone} error:`;
    private static _updatingDrone = "Updating Drone";
    private static _updatingDroneError = `${UpdateDroneComponent._updatingDrone} error:`;

    private _drone: RegisteredDrone = new RegisteredDrone();

    public ngAfterViewInit() {
        const state: UpdateDroneState | undefined = this.stateManager.updateDroneState;

        if (state !== undefined) {
            this._drone = state.drone;
            this.droneComponent.restoreState(state.droneComponentState);
            super.restoreState(state.baseComponentState);
        } else {
            this.droneComponent.init();
            this.loadDrone(this.id);
        }
    }

    public ngOnDestroy() {
        const state: UpdateDroneState = {} as UpdateDroneState;
        state.drone = this._drone;
        state.droneComponentState = this.droneComponent.getState();
        state.baseComponentState = super.getState();
        this.stateManager.updateDroneState = state;
    }

    protected async loadDrone(id: number) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.droneService.getDrone(await getIdToken(currentUser), id);
            this.message = "";
            super.showSnackBar(`${UpdateDroneComponent._retrievingDrone}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["get-drone"] == "success") {
                        this._drone = RegisteredDrone.toRegisteredDrone(json["drone"]);
                        this.droneComponent.populateFormFromModel(this._drone);
                        this.message = "";
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${UpdateDroneComponent._retrievingDroneError} ${(<Error>error).message}`;
                    this.message = message;
                }
                super.dismissSnackBar();
            })
        }
    }

    protected cancel(): void {
        this.router.navigate(["/view-drones"], { state: { restore: true } });
    }

    protected async save() {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            this._drone = this.droneComponent.createModelFromForm(this._drone);

            const observable = await this.droneService.updateDrone(await getIdToken(currentUser), this._drone);
            this.message = "";
            super.showSnackBar(`${UpdateDroneComponent._updatingDrone}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["update-drone"] == "success") {
                        const queryParam = `Drone '${this._drone.serial_number}' updated.`;
                        this.router.navigate(["/view-drones"], { queryParams: {"overrideSnackBar": queryParam}, state: { refresh: true } });
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${UpdateDroneComponent._updatingDroneError} ${(<Error>error).message}`;
                    this.message = message;
                    super.dismissSnackBar();
                }
            })
        } else {
            const message = `${UpdateDroneComponent._updatingDroneError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }
}
